const STORAGE_KEY = "incubator.content-translations.v1";
const MAX_ENTRIES = 150;
const MAX_MEMORY_ENTRIES = 5000;
const MAX_STORAGE_CHARS = 1000000;
const MAX_SOURCE_CHARS = 50000;
const MAX_AGE = 30 * 24 * 60 * 60 * 1000;
const keyOf = (source, target, format) => `${target}\u0000${format}\u0000${source}`;

// This cache contains only content passed explicitly through $content/$contentHtml.
// Neither API responses nor form values are modified or scanned automatically.
export function createLiveTranslator({
  endpoint = "/translations/translate",
  request = (...args) => fetch(...args),
  storage,
  onUpdate = () => {},
  now = Date.now,
  concurrency = 2,
  batchSize = 1,
  timeout = 120000,
  cacheOnServer = true,
  retryDelays = [2000, 8000, 30000],
  setRetryTimer = (callback, delay) => { const timer = setTimeout(callback, delay); timer.unref?.(); return timer; },
  clearRetryTimer = clearTimeout,
} = {}) {
  const cache = new Map();
  const jobs = new Map();
  const failures = new Map();
  const queue = [];
  let active = 0;
  let currentTarget = "pt";
  let unavailable = false;
  let scheduled = false;
  let retryTimer = null;

  function state() {
    return {
      pending: [...jobs.values()].filter((job) => job.target === currentTarget).length,
      failed: [...failures.keys()].some((key) => key.startsWith(`${currentTarget}\u0000`)),
      unavailable,
    };
  }
  function notify() { onUpdate(state()); }
  function markFailure(job, retryable, attempted = true) {
    const previous = failures.get(job.key);
    failures.set(job.key, { job, retryable, attempts: (previous?.attempts || 0) + (attempted ? 1 : 0) });
  }
  function scheduleFailures(minimumDelay = 0) {
    const eligible = () => [...failures.values()].filter(({ job, retryable, attempts }) =>
      job.target === currentTarget && retryable && attempts <= retryDelays.length && !jobs.has(job.key));
    const pending = eligible();
    if (!retryDelays.length || !pending.length || currentTarget === "pt") return;
    if (retryTimer !== null) {
      if (!minimumDelay) return;
      clearRetryTimer(retryTimer);
    }
    const attempt = Math.max(0, Math.min(...pending.map((item) => item.attempts)) - 1);
    retryTimer = setRetryTimer(() => {
      retryTimer = null;
      unavailable = false;
      for (const { job } of eligible()) { jobs.set(job.key, job); queue.push(job); }
      notify();
      schedule();
    }, Math.max(minimumDelay, retryDelays[attempt] || 0));
  }
  function persist() {
    let size = 0;
    const entries = [];
    for (const entry of [...cache.values()].reverse()) {
      size += entry.source.length + entry.translation.length;
      if (size > MAX_STORAGE_CHARS || entries.length >= MAX_ENTRIES) break;
      entries.push(entry);
    }
    try { storage?.setItem(STORAGE_KEY, JSON.stringify(entries.reverse())); } catch { /* memory cache still works */ }
  }
  try {
    const saved = JSON.parse(storage?.getItem(STORAGE_KEY) || "[]");
    if (Array.isArray(saved)) for (const item of saved.slice(-MAX_ENTRIES)) {
      if (typeof item.source === "string" && item.source.length <= MAX_SOURCE_CHARS &&
          ["en", "fr"].includes(item.target) && ["text", "html"].includes(item.format) &&
          typeof item.translation === "string" && item.translation.trim() &&
          Number.isFinite(item.savedAt) && now() - item.savedAt < MAX_AGE) {
        cache.set(keyOf(item.source, item.target, item.format), item);
      }
    }
  } catch { /* corrupted or disabled storage must not prevent reading news */ }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => { scheduled = false; pump(); });
  }

  async function run(group) {
    const first = group[0];
    const controller = new AbortController();
    let retryDelay = 0;
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await request(endpoint, {
        method: "POST", credentials: "omit",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          q: group.length === 1 ? first.source : group.map((job) => job.source),
          source: "pt", target: first.target, format: first.format,
          ...(cacheOnServer ? {} : { cache: false }),
        }),
      });
      if (!response.ok) {
        const error = new Error(`Translation HTTP ${response.status}`);
        error.unavailable = response.status >= 500 || [403, 404, 429].includes(response.status);
        error.retryable = response.status >= 500 || response.status === 429;
        error.retryAfter = Math.min(60000, (Number(response.headers?.get("Retry-After")) || 0) * 1000);
        throw error;
      }
      const data = await response.json();
      const values = group.length === 1 ? [data.translatedText] : data.translatedText;
      if (!Array.isArray(values) || values.length !== group.length || values.some((value) => value !== null && (typeof value !== "string" || !value.trim()))) {
        throw new Error("Invalid translation");
      }
      group.forEach((job, index) => {
        if (values[index] === null) { markFailure(job, true); return; }
        failures.delete(job.key);
        cache.set(job.key, {
          source: job.source, target: job.target, format: job.format,
          translation: values[index], savedAt: now(),
        });
      });
      // The persistence limit must not evict text still rendered on a large page.
      while (cache.size > MAX_MEMORY_ENTRIES) cache.delete(cache.keys().next().value);
      persist();
    } catch (error) {
      const retryable = error.retryable || error.name === "AbortError" || error instanceof TypeError;
      group.forEach((job) => markFailure(job, retryable));
      // Pause an unavailable service, then retry with bounded backoff. One
      // failed fragment must never permanently disable other pages.
      if (error.unavailable || error.name === "AbortError" || error instanceof TypeError) {
        unavailable = true;
        for (const pending of queue.splice(0)) { jobs.delete(pending.key); markFailure(pending, retryable, false); }
      }
      retryDelay = error.retryAfter || 0;
    } finally {
      clearTimeout(timer);
      group.forEach((job) => jobs.delete(job.key));
      active--;
      notify();
      schedule();
      scheduleFailures(retryDelay);
    }
  }

  function pump() {
    // Titles and short visible text should not wait behind complete articles.
    queue.sort((a, b) => (a.format === "html") - (b.format === "html") || a.source.length - b.source.length);
    if (!unavailable) while (active < concurrency && queue.length) {
      const job = queue.shift();
      const group = [job];
      let characters = job.source.length;
      for (let index = 0; index < queue.length && group.length < batchSize;) {
        const next = queue[index];
        if (next.target === job.target && next.format === job.format && characters + next.source.length <= 8000) {
          group.push(queue.splice(index, 1)[0]);
          characters += next.source.length;
        } else index++;
      }
      active++;
      void run(group);
    }
    notify();
  }

  return {
    get(source, target, format = "text") {
      if (typeof source !== "string") return "";
      if (target === "pt" || !source.trim() || !["en", "fr"].includes(target)) return source;
      const key = keyOf(source, target, format);
      const cached = cache.get(key);
      if (cached && now() - cached.savedAt < MAX_AGE) return cached.translation;
      if (!jobs.has(key) && !failures.has(key) && !unavailable) {
        if (source.length > MAX_SOURCE_CHARS) { markFailure({ key, source, target, format }, false); schedule(); return source; }
        const job = { key, source, target, format };
        jobs.set(key, job);
        queue.push(job);
        schedule();
      }
      return source;
    },
    activate(target) {
      if (target !== currentTarget && retryTimer !== null) { clearRetryTimer(retryTimer); retryTimer = null; }
      if (target !== currentTarget) unavailable = false;
      currentTarget = target;
      // Requests already running may populate their own language cache safely.
      for (let index = queue.length - 1; index >= 0; index--) {
        if (queue[index].target !== currentTarget) jobs.delete(queue.splice(index, 1)[0].key);
      }
      queueMicrotask(notify);
      scheduleFailures();
    },
    retry() {
      if (retryTimer !== null) { clearRetryTimer(retryTimer); retryTimer = null; }
      failures.clear();
      unavailable = false;
      notify();
    },
    state,
  };
}
